import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../styles/ChatMessage.css';
import RetrievalPathPanel from './RetrievalPathPanel';
import ContributingChunks from './ContributingChunks';
import FeedbackDialog from './FeedbackDialog';
import apiService from '../services/api';

function ChatMessage({ message, onFeedback, theme }) {
  const [copySuccess, setCopySuccess] = useState(false);

  // Copy response content to clipboard
  const handleCopyResponse = async () => {
    try {
      await navigator.clipboard.writeText(messageText || '');
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1200);
    } catch (err) {
      setCopySuccess(false);
    }
  };
  const isUser = message.sender === 'user';
  const isSystemMessage = !isUser && message.isSystemMessage;
  const [feedbackHover, setFeedbackHover] = useState(null);
  const [feedbackAnimation, setFeedbackAnimation] = useState(null);
  const [showSources, setShowSources] = useState(false);
  const [showRetrievalPath, setShowRetrievalPath] = useState(false);
  const [retrievalPathData, setRetrievalPathData] = useState(null);
  const [isLoadingGraph, setIsLoadingGraph] = useState(false);
  const [showChunkSettings, setShowChunkSettings] = useState(false);
  const [showContributingChunks, setShowContributingChunks] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  
  // Function to fetch retrieval path data when toggle is clicked
  const fetchRetrievalPath = async () => {
    if (!message.retrievalId) return;
    
    setIsLoadingGraph(true);
    try {
      const data = await apiService.getRetrievalPath(message.retrievalId);
      setRetrievalPathData(data);
      // Open the panel after data is loaded
      setShowRetrievalPath(true);
    } catch (error) {
      console.error('Error fetching retrieval path:', error);
    } finally {
      setIsLoadingGraph(false);
    }
  };
    // Toggle retrieval path panel visibility and load data if needed
  const handleToggleRetrievalPath = () => {
    // Don't proceed if there's no retrieval ID
    if (!message.retrievalId) return;
    
    if (!showRetrievalPath) {
      // If we're opening the panel and don't have data yet, fetch it
      if (!retrievalPathData && message.retrievalId) {
        fetchRetrievalPath();
      } else {
        setShowRetrievalPath(true);
      }
    } else {
      setShowRetrievalPath(false);
    }
  };

  // Close the retrieval path panel
  const closeRetrievalPathPanel = () => {
    setShowRetrievalPath(false);
  };
  
  // Extract source links if they exist in the message text
  const extractSources = () => {
    if (!isUser && message.text) {
      // Only extract sources from explicit "Source URL:" patterns or standalone URLs at the end
      // Do NOT extract inline markdown links - those should remain in the message text
      
      let sources = [];
      let match;
      
      // Find the source citation section — matches "#### Source(s):" or "### Source URL:" (2-4 hashes)
      const sourceUrlSectionMatch = message.text.match(/#{2,4}\s*Source(?:\(s\)|s?\s*URL)?\s*:?[\s\S]*$/i);
      
      if (sourceUrlSectionMatch) {
        const sourceSection = sourceUrlSectionMatch[0];
        
        // First, extract all markdown-formatted links: [text](url)
        const markdownLinkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
        
        while ((match = markdownLinkPattern.exec(sourceSection)) !== null) {
          const linkText = match[1].trim();
          const linkUrl = match[2].trim();
          
          // Check if it's a valid URL or a file reference
          if (linkUrl && linkUrl !== '#') {
            // Use the URL if it's valid
            const sourceEntry = { url: linkUrl, text: linkText };
            if (!sources.some(s => s.url === linkUrl && s.text === linkText)) {
              sources.push(sourceEntry);
            }
          } else {
            // If URL is just '#', use the link text as the source
            const sourceEntry = { url: linkText, text: linkText };
            if (!sources.some(s => s.text === linkText)) {
              sources.push(sourceEntry);
            }
          }
        }
        
        // Remove all markdown links from sourceSection for next processing
        let sourceSectionWithoutMarkdown = sourceSection.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '');
        
        // Extract plain text sources (lines that contain text but are not markdown)
        // Split by lines and process each line
        const lines = sourceSectionWithoutMarkdown.split('\n');
        
        for (let line of lines) {
          line = line.trim();
          
          // Skip empty lines and the source section header line
          if (!line || line.match(/^#{2,4}\s*Source(?:\(s\)|s?\s*URL)?\s*:?/i)) {
            continue;
          }
          
          // Check for plain URLs (http/https)
          const urlMatch = line.match(/^(https?:\/\/[^\s]+)/);
          if (urlMatch) {
            const url = urlMatch[1].trim();
            if (!sources.some(s => s.url === url)) {
              sources.push({ url: url, text: url });
            }
            continue;
          }
          
          // Check for file names with common extensions (including filenames with spaces)
          // Match everything up to and including the file extension
          const fileMatch = line.match(/^(.+\.(pdf|txt|html|md|doc|docx|xls|xlsx|ppt|pptx))$/i);
          if (fileMatch) {
            const fileName = fileMatch[1].trim();
            if (!sources.some(s => s.text === fileName)) {
              sources.push({ url: fileName, text: fileName });
            }
            continue;
          }
          
          // Check for other plain text references (page numbers, document references)
          // Format like: "BigFix Inventory | 2 - Overview | 97"
          const textReferenceMatch = line.match(/^(.+)$/);
          if (textReferenceMatch && line.length > 0) {
            const reference = textReferenceMatch[1].trim();
            // Only add if it's not already captured and has meaningful content
            if (reference && !sources.some(s => s.text === reference)) {
              sources.push({ url: reference, text: reference });
            }
          }
        }
      }
      
      // Also directly check for the message.sourceUrls property from App.js
      if (message.sourceUrls && Array.isArray(message.sourceUrls) && message.sourceUrls.length > 0) {
        message.sourceUrls.forEach(url => {
          // Skip malformed entries — only accept absolute http(s) URLs
          if (!url || !url.startsWith('http')) return;
          if (!sources.some(s => s.url === url || s.text === url)) {
            sources.push({ url: url, text: url.split('/').pop() || url });
          }
        });
      }
      
      return sources;
    }
    return [];
  };
  
  // Clean message text by removing source links
  const cleanMessageText = () => {
    if (!isUser && message.text) {
      // Remove explicit source sections from message text, but keep inline markdown links
      let cleanedText = message.text;
      
      // Remove the entire "#### Source URL:" section and everything after it
      cleanedText = cleanedText.replace(/####\s*Source\s*URL:[\s\S]*$/i, '');
      
      // Remove the source citation section ("#### Source(s):" or "### Source URL:") and everything after it
      cleanedText = cleanedText.replace(/#{2,4}\s*Source(?:\(s\)|s?\s*URL)?\s*:?[\s\S]*$/i, '');
      
      // Clean up extra newlines and spaces left after removing sources
      cleanedText = cleanedText.replace(/\n{3,}/g, '\n\n');
      cleanedText = cleanedText.replace(/\n+$/g, ''); // Remove trailing newlines
      cleanedText = cleanedText.trim();
      
      return cleanedText;
    }
    return message.text;
  };
  
  const sources = extractSources();
  const messageText = cleanMessageText();
  // Check if the message has contributing chunks and they actually have content (define early)
  const hasContributingChunks = !isUser && message.contributingChunks && message.contributingChunks.length > 0;
  // Determine presence of source URLs (markdown or plain) to gate other UI elements
  const hasSources = sources && sources.length > 0;
  // Gate contributing chunks & retrieval path on sources per requirement
  const gatedHasContributingChunks = hasSources && hasContributingChunks;
  const gatedHasRetrievalId = hasSources && !!message.retrievalId;

  // If sources disappear (edge case), collapse gated panels
  useEffect(() => {
    if (!hasSources) {
      if (showContributingChunks) setShowContributingChunks(false);
      if (showRetrievalPath) setShowRetrievalPath(false);
    }
  }, [hasSources]);
    const handleFeedbackClick = (feedbackType) => {
    if (onFeedback) {
      // Set animation state
      setFeedbackAnimation(feedbackType);
      
      // Remove animation after it completes
      setTimeout(() => {
        setFeedbackAnimation(null);
      }, 1000);
      
      if (feedbackType === 'Dislike') {
        // Open feedback dialog for negative feedback
        setFeedbackDialogOpen(true);
      } else {
        // Send positive feedback directly
        onFeedback(message.id, feedbackType);
      }
    }
  };
  
  // Handle feedback submission from dialog
  const handleFeedbackSubmit = (feedbackDetails) => {
    // Call the onFeedback function with the feedback details
    onFeedback(message.id, 'Dislike', feedbackDetails.type, feedbackDetails.comment, feedbackDetails.name);
  };
  
  return (
    <div className={`message-container ${isUser ? 'user-message' : 'ai-message'}`}>
      {!isUser && <div className="message-avatar"></div>}
      
      <div className={`message ${isUser ? 'user' : 'ai'} ${message.isError ? 'error' : ''} ${isSystemMessage ? 'isSystemMessage' : ''}`}>
        {message.files && message.files.length > 0 && (
          <div className="message-files">
            {message.files.map(file => (
              <div key={file.id} className="file-attachment">
                <span className="material-icons">description</span>
                <span>{file.name}</span>
              </div>
            ))}
          </div>
        )}
        
        <div className="message-text">
          {isUser ? (
            message.text
          ) : (
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer" />
                ),
                img: ({ node, alt, src, ...props }) => {
                  // Extract filename from src if available
                  const imageName = alt || src?.split('/').pop() || 'Image';
                  return (
                    <a 
                      href={src} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="image-link"
                      title={`View: ${imageName}`}
                    >
                      🖼️ {imageName}
                    </a>
                  );
                }
              }}
            >
              {messageText}
            </ReactMarkdown>
          )}
            {/* Enhanced source information section - show when we have sources or other info */}
      {!isUser && ((hasSources) || message.chunkSettings) && (
            <div className="enhanced-info-container">
              {/* Source Documentation toggle button */}
        {hasSources && (
                <button 
                  className="info-toggle-btn source-toggle-btn"
                  onClick={() => setShowSources(!showSources)}
                >
                  <span className="material-icons">source</span>
                  Source Documentation
                  <span className="material-icons">{showSources ? 'expand_less' : 'expand_more'}</span>
                </button>
              )}
              
              {/* Retrieval Path toggle button - show when retrieval ID exists */}
        {gatedHasRetrievalId && (
                <button 
                  className="info-toggle-btn retrieval-path-toggle-btn"
                  onClick={handleToggleRetrievalPath}
                  disabled={isLoadingGraph}
                >
                  <span className="material-icons">account_tree</span>
                  Retrieval Path
                  {isLoadingGraph ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    <span className="material-icons">{showRetrievalPath ? 'expand_less' : 'expand_more'}</span>
                  )}
                </button>
              )}
              
              {/* Contributing Chunks toggle button - show when contributing chunks exist */}
        {gatedHasContributingChunks && (
                <button 
                  className="info-toggle-btn chunks-toggle-btn"
                  onClick={() => setShowContributingChunks(!showContributingChunks)}
                >
                  <span className="material-icons">layers</span>
                  Contributing Chunks
                  <span className="material-icons">{showContributingChunks ? 'expand_less' : 'expand_more'}</span>
                </button>
              )}
                {/* Chunk Settings toggle button - show when chunk settings exist */}
              {message.chunkSettings && (
                <button 
                  className="info-toggle-btn chunk-settings-toggle-btn"
                  onClick={() => setShowChunkSettings(!showChunkSettings)}
                >
                  <span className="material-icons">settings</span>
                  Chunk Settings
                  <span className="material-icons">{showChunkSettings ? 'expand_less' : 'expand_more'}</span>
                </button>
              )}
            </div>
          )}
          
          {/* Sources section - integrated within the message */}
          {!isUser && sources.length > 0 && showSources && (
            <div className="sources-container-integrated">
              <div className="sources-header">
                <span className="material-icons">source</span>
                <span>Sources</span>
              </div>
              <div className="sources-list">
                {sources.map((source, index) => (
                  <div key={index} className="source-item">
                    <span className="material-icons">article</span>
                    <a 
                      href={source.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="source-link"
                    >
                      {source.text}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
            {/* Contributing Chunks section - show when contributing chunks exist */}
          {!isUser && showContributingChunks && gatedHasContributingChunks && (
            <div className="contributing-chunks-section">
              <ContributingChunks 
                chunks={message.contributingChunks || []}
                theme={theme || 'light'}
              />
            </div>
          )}
            {/* Chunk Settings section - show when chunk settings exist */}
          {!isUser && showChunkSettings && message.chunkSettings && (
            <div className="retrieval-path-settings">
              <div className="retrieval-path-settings-title">
                <span className="material-icons">settings</span>
                <span>Chunk Settings</span>
              </div>
              <div className="retrieval-path-settings-grid">
                <div className="setting-item">
                  <span className="material-icons">segment</span>
                  <span>Chunk Size: {message.chunkSettings.chunkSize || 'Default'}</span>
                </div>
                <div className="setting-item">
                  <span className="material-icons">swap_horiz</span>
                  <span>Chunk Overlap: {message.chunkSettings.chunkOverlap || 'Default'}</span>
                </div>
                <div className="setting-item">
                  <span className="material-icons">table_chart</span>
                  <span>Table Handling: {message.chunkSettings.tableHandling || 'Default'}</span>
                </div>
                <div className="setting-item">
                  <span className="material-icons">code</span>
                  <span>Code Block Handling: {message.chunkSettings.codeBlockHandling || 'Default'}</span>
                </div>
                <div className="setting-item">
                  <span className="material-icons">format_size</span>
                  <span>Min Tokens: {message.chunkSettings.minTokens || 'Default'}</span>
                </div>
                <div className="setting-item">
                  <span className="material-icons">format_size</span>
                  <span>Max Tokens: {message.chunkSettings.maxTokens || 'Default'}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {isUser && <div className="message-avatar user"></div>}
      
      {/* Feedback buttons below AI message */}
      {!isUser && !isSystemMessage && onFeedback && (
        <div className="message-feedback-buttons">
          <button 
            className={`feedback-btn ${feedbackAnimation === 'Like' ? 'feedback-animation' : ''} ${message.feedback === 'Like' ? 'active' : ''}`}
            onMouseEnter={() => setFeedbackHover('Like')}
            onMouseLeave={() => setFeedbackHover(null)} 
            onClick={() => handleFeedbackClick('Like')}
            title="This was helpful"
            aria-label="Thumbs up - This was helpful"
          >
            <span className="material-icons feedback-icon">thumb_up</span>
          </button>
          <button 
            className={`feedback-btn ${feedbackAnimation === 'Dislike' ? 'feedback-animation' : ''} ${message.feedback === 'Dislike' ? 'active' : ''}`}
            onMouseEnter={() => setFeedbackHover('Dislike')}
            onMouseLeave={() => setFeedbackHover(null)}  
            onClick={() => handleFeedbackClick('Dislike')}
            title="This wasn't helpful"
            aria-label="Thumbs down - This wasn't helpful"
          >
            <span className="material-icons feedback-icon">thumb_down</span>
          </button>
          {/* <button
            className="feedback-btn copy-btn"
            onClick={handleCopyResponse}
            title={copySuccess ? "Copied!" : "Copy response"}
            aria-label="Copy response"
          >
            <span className="material-icons feedback-icon">content_copy</span>
          </button> */}
        </div>
      )}
  {showRetrievalPath && gatedHasRetrievalId && (
        <RetrievalPathPanel
          retrievalData={retrievalPathData}
          onClose={closeRetrievalPathPanel}
          theme={theme || 'light'}
        />
      )}
      
      {/* Feedback Dialog for thumbs down feedback */}
      <FeedbackDialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
}

export default ChatMessage;